/**
 * Referral Share Card — dashboard viral acquisition widget.
 * Shows a preview of the user's auto-generated 1080×1920 Instagram-story-sized PNG
 * with QR code + username referral link. One-tap WhatsApp & Instagram share.
 */
import React, { useEffect, useState } from 'react';
import { Share2, MessageCircle, Download, RefreshCw, Copy, Check, TrendingUp, Users, MousePointer, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const BACKEND = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const StatTile = ({ icon: Icon, label, value, sub, color, testid }) => (
  <div className={`rounded-xl border ${color.border} ${color.bg} p-2.5`} data-testid={testid}>
    <div className="flex items-center gap-1.5 mb-1">
      <Icon className={`w-3.5 h-3.5 ${color.icon}`} />
      <div className="text-[9px] font-bold uppercase tracking-wider text-slate-300/80">{label}</div>
    </div>
    <div className={`text-2xl font-black leading-none ${color.text}`}>{value}</div>
    {sub && <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div>}
  </div>
);

const ReferralShareCard = ({ user }) => {
  const [meta, setMeta] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [bust, setBust] = useState(0);
  const [statsAnim, setStatsAnim] = useState(false);

  useEffect(() => {
    api.get('/referral/my-card')
      .then((r) => setMeta(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Poll live stats every 30s
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get('/referral/live-stats');
        if (!alive) return;
        setStats((prev) => {
          // Flash animation when any counter increased
          if (prev && (
            r.data.signups_today > prev.signups_today ||
            r.data.link_clicks_today > prev.link_clicks_today ||
            r.data.at_earned_today > prev.at_earned_today
          )) {
            setStatsAnim(true);
            setTimeout(() => setStatsAnim(false), 1500);
          }
          return r.data;
        });
      } catch (_) {}
    };
    load();
    const iv = setInterval(load, 30000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  if (loading || !meta) {
    return (
      <div className="mb-6 rounded-2xl border-2 border-amber-400/40 bg-slate-900 p-5 flex items-center gap-3">
        <div className="animate-pulse text-slate-400 text-sm">Preparing your referral card…</div>
      </div>
    );
  }

  const cardUrl = `${meta.card_png}?v=${bust}`;

  const regen = async () => {
    setRefreshing(true);
    try {
      await api.post('/referral/share-card/regenerate');
      setBust(Date.now());
      toast.success('Card regenerated');
    } catch (_) { toast.error('Failed to regenerate'); }
    finally { setRefreshing(false); }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(meta.deep_link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Referral link copied');
    } catch (_) { toast.error('Copy failed'); }
  };

  return (
    <div className="mb-6 rounded-2xl border-2 border-amber-400/50 bg-gradient-to-br from-amber-500/15 via-orange-500/10 to-fuchsia-500/15 p-4 sm:p-5" data-testid="referral-share-card">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-xl bg-amber-500 flex items-center justify-center">
          <Share2 className="w-5 h-5 text-black" />
        </div>
        <div className="flex-1">
          <div className="text-[10px] font-bold uppercase tracking-wider text-amber-300">Viral Referral · Earn AT on 7 levels</div>
          <div className="text-base sm:text-lg font-extrabold text-amber-200 leading-tight">Your Personal Share Card</div>
        </div>
        {stats && stats.rank_today && (
          <div className="text-right">
            <div className="text-[9px] uppercase tracking-wider text-amber-300/80 font-bold">Rank today</div>
            <div className="text-xl font-black text-amber-200 inline-flex items-center gap-1">
              <Trophy className="w-4 h-4" /> #{stats.rank_today}
            </div>
          </div>
        )}
      </div>

      {/* 🆕 LIVE STATS TILES */}
      {stats && (
        <div className={`grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4 ${statsAnim ? 'anant-pulse-soft' : ''}`} data-testid="referral-live-stats">
          <StatTile icon={TrendingUp} label="AT this hour" value={stats.at_earned_this_hour}
            sub={`${stats.at_earned_today} today`} testid="stat-at-hour"
            color={{ border: 'border-emerald-400/40', bg: 'bg-emerald-500/10', text: 'text-emerald-200', icon: 'text-emerald-300' }} />
          <StatTile icon={Users} label="Signups today" value={stats.signups_today}
            sub={`${stats.signups_total} total`} testid="stat-signups"
            color={{ border: 'border-cyan-400/40', bg: 'bg-cyan-500/10', text: 'text-cyan-200', icon: 'text-cyan-300' }} />
          <StatTile icon={MousePointer} label="Link clicks today" value={stats.link_clicks_today}
            sub={`${stats.link_clicks_total} total`} testid="stat-clicks"
            color={{ border: 'border-fuchsia-400/40', bg: 'bg-fuchsia-500/10', text: 'text-fuchsia-200', icon: 'text-fuchsia-300' }} />
          <StatTile icon={Trophy} label="Lifetime AT" value={stats.at_earned_total}
            sub="from referrals" testid="stat-at-total"
            color={{ border: 'border-amber-400/40', bg: 'bg-amber-500/10', text: 'text-amber-200', icon: 'text-amber-300' }} />
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Card preview */}
        <div className="sm:col-span-1">
          <div className="rounded-xl overflow-hidden border border-white/10 bg-slate-950 shadow-lg" data-testid="referral-card-preview">
            <img
              src={cardUrl}
              alt={`${meta.name}'s referral card`}
              className="w-full h-auto"
              style={{ aspectRatio: '9/16' }}
            />
          </div>
          <div className="mt-2 text-[10px] text-center text-amber-100/70">
            1080×1920 · Instagram Story sized
          </div>
        </div>

        {/* Actions */}
        <div className="sm:col-span-2 flex flex-col gap-2.5">
          <div className="rounded-xl bg-slate-950/60 border border-white/10 p-3">
            <div className="text-[10px] uppercase tracking-wider text-amber-300 font-bold">Referral Code</div>
            <div className="text-2xl font-black text-amber-200 tracking-wider mt-0.5" data-testid="referral-code">{meta.referral_code}</div>
            <div className="text-[10px] text-slate-400 font-mono break-all mt-1">{meta.deep_link}</div>
          </div>

          <a href={meta.whatsapp_url} target="_blank" rel="noreferrer" data-testid="referral-whatsapp"
            className="px-4 py-3 rounded-xl bg-green-500 hover:bg-green-400 text-black font-extrabold text-sm flex items-center justify-center gap-2 shadow-lg">
            <MessageCircle className="w-5 h-5" /> Share on WhatsApp
          </a>

          <div className="grid grid-cols-3 gap-2">
            <a href={cardUrl} download={`${meta.username}-anant.png`} data-testid="referral-download"
              className="px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-amber-200 text-xs font-bold flex items-center justify-center gap-1.5 border border-white/10">
              <Download className="w-4 h-4" /> Save
            </a>
            <button onClick={copyLink} data-testid="referral-copy-link"
              className="px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-amber-200 text-xs font-bold flex items-center justify-center gap-1.5 border border-white/10">
              {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />} {copied ? 'Copied' : 'Copy'}
            </button>
            <button onClick={regen} disabled={refreshing} data-testid="referral-regenerate"
              className="px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-amber-200 text-xs font-bold flex items-center justify-center gap-1.5 border border-white/10 disabled:opacity-50">
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>

          <div className="text-[11px] text-amber-100/70 mt-1 leading-relaxed">
            Har jo doston ke saath share karo → jab wo sign up karte hain aap ke referral se, 7-level Webchain rewards aap tak automatically pahuchte hain. Stats live update hote hain — refresh nahi karna hai!
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralShareCard;
